import { Button, cn } from '@sunstone/ui';
import type { HelpCategory } from './types';

interface HelpTocProps {
  categories: HelpCategory[];
  activeId: string | null;
  onSelect: (id: string) => void;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

/** Doubles as category navigation and "on this page" scrollspy — there is no separate category filter. */
export function HelpToc({
  categories,
  activeId,
  onSelect,
  orientation = 'vertical',
  className,
}: HelpTocProps) {
  if (categories.length === 0) {
    return null;
  }

  const isHorizontal = orientation === 'horizontal';

  return (
    <nav
      aria-label="On this page"
      className={cn(
        isHorizontal ? 'flex gap-2 overflow-x-auto pb-1' : 'flex flex-col gap-1',
        className,
      )}
    >
      {!isHorizontal && (
        <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          On this page
        </p>
      )}
      {categories.map((category) => {
        const Icon = category.icon;
        const isActive = category.id === activeId;
        return (
          <Button
            key={category.id}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onSelect(category.id)}
            className={cn(
              'gap-2',
              isHorizontal ? 'shrink-0 rounded-full' : 'w-full justify-start px-2',
              isActive
                ? 'bg-muted font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {category.label}
          </Button>
        );
      })}
    </nav>
  );
}
